import useProviderType from '@app/src/hooks/useProviderType'
import type { Provider } from '@app/src/types/organizations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { formatLevelLabel } from '@app/src/utils/assessmentLevelTranslations'
import { CheckCircleOutlined, CheckCircleRounded, ErrorOutline, LinkOff } from '@mui/icons-material'
import { CircularProgress, Stack, Typography } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { ProviderBuckets } from '../bulkCreateExpectationLogic'
import { bucketProviders } from '../bulkCreateExpectationLogic'
import type { BulkCreateExpectationFormData } from '../types'
import { BulkCreateExpectationFormField } from '../types'
import AccordionSection from './AccordionSection'

type SelectedCompaniesStepProps = {
  providers: Provider[]
  assessmentTemplate?: AssessmentTemplate
  coveredProviderIds: ReadonlySet<number>
  isLoadingCoveredData?: boolean
}

const SelectedCompaniesStep: React.FC<SelectedCompaniesStepProps> = ({
  providers,
  assessmentTemplate,
  coveredProviderIds,
  isLoadingCoveredData = false,
}) => {
  const { formatMessage } = useIntl()
  const { watch, setValue } = useFormContext<BulkCreateExpectationFormData>()
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const providerType = useProviderType()
  const providerIds = watch(BulkCreateExpectationFormField.ProviderIds)
  const templateId = watch(BulkCreateExpectationFormField.AssessmentTemplateId)
  const targetLevel = watch(BulkCreateExpectationFormField.TargetAssessmentLevel)

  const isEmpty = providerIds.length === 0

  const toggleProvider = useCallback(
    (id: number) => {
      const next = providerIds.includes(id) ? providerIds.filter(pid => pid !== id) : [...providerIds, id]
      setValue(BulkCreateExpectationFormField.ProviderIds, next, { shouldValidate: true })
    },
    [providerIds, setValue],
  )

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }, [])

  const buckets = useMemo(
    () => bucketProviders(providers, templateId, targetLevel, coveredProviderIds),
    [providers, templateId, targetLevel, coveredProviderIds],
  )

  if (isLoadingCoveredData) {
    return (
      <Stack alignItems="center" justifyContent="center" py={4}>
        <CircularProgress />
      </Stack>
    )
  }

  const summaryText = assessmentTemplate
    ? formatMessage(
        { id: 'bulkCreateExpectation.steps.selectedCompanies.summaryRowDetailed' },
        {
          count: buckets.belowTarget.length,
          assessmentName: assessmentTemplate.name,
          targetLevel: formatLevelLabel(assessmentTemplate, targetLevel, formatMessage),
          providerType,
        },
      )
    : formatMessage(
        { id: 'bulkCreateExpectation.steps.selectedCompanies.summaryRow' },
        { count: providerIds.length, providerType },
      )

  return (
    <Stack>
      <AccordionSection
        isExpanded={expandedSections.has('selected')}
        onToggle={() => toggleSection('selected')}
        icon={<CheckCircleOutlined sx={{ color: 'success.dark' }} />}
        summaryText={summaryText}
        providers={buckets.belowTarget}
        templateId={templateId}
        assessmentTemplate={assessmentTemplate}
        selectable={{ selectedIds: providerIds, onToggle: toggleProvider }}
      />

      {isEmpty && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {formatMessage({ id: 'bulkCreateExpectation.steps.selectedCompanies.emptyValidation' })}
        </Typography>
      )}

      <ExcludedGroup
        buckets={buckets}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        templateId={templateId}
        assessmentTemplate={assessmentTemplate}
      />
    </Stack>
  )
}

type ExcludedGroupProps = {
  buckets: ProviderBuckets
  expandedSections: Set<string>
  toggleSection: (section: string) => void
  templateId: number | null
  assessmentTemplate?: AssessmentTemplate
}

const ExcludedGroup: React.FC<ExcludedGroupProps> = ({
  buckets,
  expandedSections,
  toggleSection,
  templateId,
  assessmentTemplate,
}) => {
  const { formatMessage } = useIntl()
  const providerType = useProviderType()
  const { notConnected, missingSelfReported, alreadyCovered, alreadyOnTarget } = buckets
  const hasAnyExcludedGroups =
    notConnected.length > 0 || missingSelfReported.length > 0 || alreadyCovered.length > 0 || alreadyOnTarget.length > 0

  const sections = useMemo(() => {
    const items: { key: string; icon: React.ReactNode; text: string; providers: Provider[]; showLevel: boolean }[] = []

    if (notConnected.length > 0) {
      items.push({
        key: 'notConnected',
        icon: <LinkOff sx={{ color: 'text.secondary' }} />,
        text: formatMessage(
          { id: 'bulkCreateExpectation.steps.selectedCompanies.notConnected' },
          { count: notConnected.length, providerType },
        ),
        providers: notConnected,
        showLevel: false,
      })
    }

    if (missingSelfReported.length > 0) {
      items.push({
        key: 'missingSelfReported',
        icon: <ErrorOutline sx={{ color: 'semantic.error' }} />,
        text: formatMessage(
          { id: 'bulkCreateExpectation.steps.selectedCompanies.missingSelfReported' },
          { count: missingSelfReported.length, providerType },
        ),
        providers: missingSelfReported,
        showLevel: false,
      })
    }

    if (alreadyCovered.length > 0) {
      items.push({
        key: 'alreadyCovered',
        icon: <CheckCircleOutlined sx={{ color: 'success.dark' }} />,
        text: formatMessage(
          { id: 'bulkCreateExpectation.steps.selectedCompanies.alreadyCovered' },
          { count: alreadyCovered.length, providerType },
        ),
        providers: alreadyCovered,
        showLevel: true,
      })
    }

    if (alreadyOnTarget.length > 0) {
      items.push({
        key: 'alreadyOnTarget',
        icon: <CheckCircleRounded sx={{ color: 'success.dark' }} />,
        text: formatMessage(
          { id: 'bulkCreateExpectation.steps.selectedCompanies.alreadyOnTarget' },
          { count: alreadyOnTarget.length, providerType },
        ),
        providers: alreadyOnTarget,
        showLevel: true,
      })
    }

    return items
  }, [buckets, providerType, formatMessage])

  return (
    <>
      {hasAnyExcludedGroups && (
        <Typography variant="subtitle1" mt={4} mb={2}>
          {formatMessage({ id: 'bulkCreateExpectation.steps.selectedCompanies.excludedTitle' })}
        </Typography>
      )}
      {sections.map((section, index) => (
        <AccordionSection
          key={section.key}
          isExpanded={expandedSections.has(section.key)}
          onToggle={() => toggleSection(section.key)}
          icon={section.icon}
          summaryText={section.text}
          providers={section.providers}
          templateId={templateId}
          assessmentTemplate={assessmentTemplate}
          showLevel={section.showLevel}
          isFirst={index === 0}
          isLast={index === sections.length - 1}
        />
      ))}
    </>
  )
}

export default SelectedCompaniesStep
